/**
 * Public JSON-RPC endpoints per chain, in preference order.
 *
 * Three very different consumers need this same list — the server client
 * (`lib/chain.ts`), the browser client (`lib/chain.client.ts`) and the
 * `connect-src` directive in `proxy.ts` — and when they drift the symptom is a
 * claim that dies at `simulateContract`: either every endpoint we know about is
 * down, or the one that is up is blocked by CSP. So the list lives here once.
 *
 * Dependency-free on purpose: `proxy.ts` runs as middleware on every request
 * and must not pull in viem or the env validation in `lib/config.ts`.
 *
 * Order matters, and it is not arbitrary. Checked 2026-08-03:
 * `bsc-testnet-rpc.publicnode.com` answered 503 "no available nodes found" and
 * `data-seed-prebsc-1-s1.binance.org` no longer resolves at all — those two
 * were the entire browser list, so testnet claims failed outright. The
 * bnbchain.org seeds lead now; publicnode stays as a last resort rather than
 * being removed, since an endpoint that is down today is usually back later.
 */

export const LOCAL_RPC_URLS: readonly string[] = ["http://127.0.0.1:8545"];

export const BSC_TESTNET_RPC_URLS: readonly string[] = [
  "https://data-seed-prebsc-1-s1.bnbchain.org:8545",
  "https://data-seed-prebsc-2-s1.bnbchain.org:8545",
  "https://bsc-testnet.drpc.org",
  "https://api.zan.top/bsc-testnet",
  "https://bsc-testnet-rpc.publicnode.com",
];

export const BSC_MAINNET_RPC_URLS: readonly string[] = [
  "https://bsc-dataseed.bnbchain.org",
  "https://bsc-dataseed1.bnbchain.org",
  "https://bsc-rpc.publicnode.com",
  "https://bsc-dataseed1.defibit.io",
];

/**
 * Both networks' origins, for the CSP.
 *
 * Listing mainnet and testnet together lets one image serve either network —
 * the chain is chosen by env at runtime, while the CSP is fixed at build time.
 * The extra origins grant nothing: they are public read-only nodes, and the
 * value of `connect-src` here is that everything *else* stays blocked.
 */
export const RPC_CONNECT_SRC: readonly string[] = Array.from(
  new Set(
    [...BSC_TESTNET_RPC_URLS, ...BSC_MAINNET_RPC_URLS].map(
      (url) => new URL(url).origin,
    ),
  ),
);
